/**
 * The screen's filter state as quality requests. Nested channel-platform pairs, affiliate child exclusions, exact
 * add-backs, no-link, every-link-but and campaigns keep their shape: nothing is flattened into one source mode.
 */
import type { HomePeriod } from '../../../mock-data/types';
import { customDays, homeWindow, isHomeInterval } from '../../home/stats-mapping';
import { pageOfKey, type WebsiteFilters } from '../filters';
import { TOP_PAGES, isPreset, type PageScope, type WebsitePeriod } from '../queries';
import { ALL_SEL, resolveMatch } from '../selection';
import type {
  QualityFacet,
  QualityFacetRequest,
  QualityFilters,
  QualityHomeRange,
  QualityHomeRequest,
  QualityRange,
  QualityReportRequest,
} from './types';

const PEOPLE = { countingUnit: 'person' as const, population: 'unflagged' as const };

/** A screen period as the wire range: presets in lower case, custom dates as they are. */
export const rangeOf = (period: WebsitePeriod): QualityRange =>
  isPreset(period) ? (period.toLowerCase() as Lowercase<typeof period>) : period;

const MS_DAY = 24 * 60 * 60 * 1000;
const dayMs = (date: string): number => Date.parse(`${date}T00:00:00Z`);
const dateOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/**
 * The fine filters; a filter at its all-selection state is omitted. The source, link and campaign filters apply only
 * where the environment credits a visit to a source or a link (`attributing`, the hosts read's flag), as the cards do.
 */
export const compileFilters = (
  picked: Omit<WebsiteFilters, 'interval' | 'hosts'>,
  attributing: boolean,
): QualityFilters => {
  const filters = attributing ? picked : { ...picked, channels: null, links: null, campaigns: ALL_SEL };
  const out: QualityFilters = {};
  const pages = resolveMatch(filters.pages);
  const items = (pages?.values ?? []).map(pageOfKey).filter((p): p is PageScope => p !== null);
  if (pages && items.length) out.pages = { mode: pages.op, items: items.map(({ kind, path }) => ({ kind, path })) };
  if (filters.channels !== null)
    out.channels = filters.channels.map((c) => ({
      channel: c.channel,
      platforms: c.platforms === 'all' ? 'all' : [...c.platforms],
      except: [...c.except],
    }));
  const l = filters.links;
  if (l !== null) {
    out.links = {
      affiliates: l.affiliates.map((a) => ({ id: a.id, exceptLinks: [...a.exceptLinks] })),
      exact: [...l.exact],
      noLink: l.noLink,
      ...(l.allExcept !== null
        ? {
            allExcept: [...l.allExcept],
            ...(l.exceptAffiliates?.length ? { exceptAffiliates: [...l.exceptAffiliates] } : {}),
          }
        : {}),
    };
  } else {
    const campaigns = resolveMatch(filters.campaigns);
    if (campaigns) out.campaigns = { mode: campaigns.op, values: [...campaigns.values] };
  }
  const countries = resolveMatch(filters.countries);
  if (countries) out.countries = { mode: countries.op, values: [...countries.values] };
  return out;
};

const withFilters = (filters: QualityFilters): { filters?: QualityFilters } =>
  Object.keys(filters).length ? { filters } : {};

/** The Website screen's report; null when no address is chosen (no request is made then). */
export const reportRequest = (filters: WebsiteFilters, attributing: boolean): QualityReportRequest | null => {
  const { interval, hosts, ...fine } = filters;
  if (hosts.kind === 'none' || (hosts.kind === 'some' && !hosts.hosts.length)) return null;
  return {
    ...PEOPLE,
    view: 'report',
    range: rangeOf(interval),
    topPagesLimit: TOP_PAGES,
    ...(hosts.kind === 'some' ? { selectedHosts: [...hosts.hosts] } : {}),
    ...withFilters(compileFilters(fine, attributing)),
  };
};

/**
 * One picker's facet over the report's effective addresses; page discovery takes no filters, and a site search goes
 * out cut by {@link siteSearch}. Throws on an empty address list, which is never sent (it would read as the default).
 */
export const facetRequest = (
  facet: QualityFacet,
  range: QualityRange,
  selectedHosts: readonly string[],
  filters: QualityFilters,
): QualityFacetRequest => {
  if (!selectedHosts.length) throw new RangeError('a facet needs at least one address');
  const base = { ...PEOPLE, view: 'facet' as const, range, selectedHosts: [...selectedHosts] };
  if (facet.name !== 'page') return { ...base, facet, ...withFilters(filters) };
  if (facet.kind === 'landing') return { ...base, facet };
  const search = siteSearch(facet.search ?? '');
  return { ...base, facet: { name: 'page', kind: 'site', ...(search ? { search } : {}) } };
};

/** Home's people, with the daily line's dates copied from Home's own window where Home draws one. */
export const homeRequest = (interval: HomePeriod, nowMs: number): QualityHomeRequest => {
  // A custom value that is not a valid range reads as the last 7 days, as Home's own window does.
  const period: HomePeriod = isHomeInterval(interval) || customDays(interval) ? interval : '7D';
  const range: QualityHomeRange = isHomeInterval(period) ? (period.toLowerCase() as Lowercase<typeof period>) : period;
  if (period !== '7D' && period !== '30D' && isHomeInterval(period)) return { ...PEOPLE, view: 'home_visitors', range };
  const win = homeWindow(period, nowMs);
  return {
    ...PEOPLE,
    view: 'home_visitors',
    range,
    seriesWindow: { fromDate: win.startDate, throughDate: dateOf(dayMs(win.endDate) - MS_DAY) },
  };
};

const MAX_ASSET_DAYS = 366;

/**
 * One landing page's views over the asset sheet's own dates (`YYYY-MM-DD`, the last held to `today`), with the brand's
 * default addresses; null over 366 days (no request, the Views dash).
 */
export const assetRequest = (
  path: string,
  fromDate: string,
  toDate: string,
  today: string,
): QualityFacetRequest | null => {
  const last = toDate > today ? today : toDate;
  if ((dayMs(last) - dayMs(fromDate)) / MS_DAY + 1 > MAX_ASSET_DAYS) return null;
  return {
    ...PEOPLE,
    view: 'facet',
    range: `${fromDate}..${last}`,
    facet: { name: 'page', kind: 'landing', candidates: [path] },
  };
};

const BATCH = 50;

/** The library's published paths as landing candidates over 90 days, in batches of at most 50 in card order. */
export const libraryBatches = (paths: readonly string[]): QualityFacetRequest[] => {
  const batches: QualityFacetRequest[] = [];
  for (let i = 0; i < paths.length; i += BATCH)
    batches.push({
      ...PEOPLE,
      view: 'facet',
      range: '90d',
      facet: { name: 'page', kind: 'landing', candidates: paths.slice(i, i + BATCH) },
    });
  return batches;
};

const MAX_SEARCH_CHARS = 100;
const MAX_SEARCH_BYTES = 300;
const utf8 = new TextEncoder();

/** A site page search as sent: trimmed, cut to 100 code points and 300 UTF-8 bytes, trimmed again. */
export const siteSearch = (search: string): string => {
  let bytes = 0;
  const kept: string[] = [];
  for (const ch of Array.from(search.trim()).slice(0, MAX_SEARCH_CHARS)) {
    bytes += utf8.encode(ch).length;
    if (bytes > MAX_SEARCH_BYTES) break;
    kept.push(ch);
  }
  return kept.join('').trim();
};
